import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Radar,
  FileText,
  Bell,
  TrendingUp,
  Sparkles,
  ArrowUpRight,
  LifeBuoy,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { LiveTrendsFeed } from "@/components/dashboard/LiveTrendsFeed";
import { NotificationSettingsPanel } from "@/components/dashboard/NotificationSettingsPanel";
import { NotificationHistoryPanel } from "@/components/dashboard/NotificationHistoryPanel";

import { TrendDetailsModal } from "@/components/dashboard/TrendDetailsModal";
import { TrendAlertsPanel } from "@/components/dashboard/TrendAlertsPanel";
import { SuccessStories } from "@/components/dashboard/SuccessStories";
import { LicenseStatusPanel } from "@/components/dashboard/LicenseStatusPanel";
import { ReferralPanel } from "@/components/dashboard/ReferralPanel";
import { ShareAnalyticsPanel } from "@/components/dashboard/ShareAnalyticsPanel";
import { ChannelAnalyticsPanel } from "@/components/dashboard/ChannelAnalyticsPanel";
import { useApprovedTrends } from "@/hooks/useApprovedTrends";
import { formatDataAge } from "@/lib/trendNotifications";

const NICHE_OPTIONS = [
  "Health & wellness",
  "Finance & investing",
  "AI & tech",
  "Beauty & fashion",
  "Home & lifestyle",
  "Education & coaching",
  "Gaming",
  "Pets",
  "Sustainability",
];

const ROLE_OPTIONS = [
  "Digital product creator",
  "E-commerce seller",
  "Content creator",
  "Marketer / agency",
];

const GOAL_OPTIONS = [
  "Launch a new product",
  "Scale an existing offer",
  "Grow content & audience",
  "Find winning ad angles",
];

const EXPERIENCE_OPTIONS = ["Just starting", "1–2 launches", "Seasoned operator"];

type Preferences = {
  role: string | null;
  niches: string[];
  primary_goal: string | null;
  experience_level: string | null;
  onboarded_at: string | null;
};

type TrendInsight = {
  niche: string;
  title: string;
  velocity: number; // 0-100
  competition: "Low" | "Medium" | "High";
  monetization: number; // 0-100
  source: string;
  blueprint: string;
};

// Deterministic per-niche insight templates so the same user sees stable picks.
const NICHE_INSIGHTS: Record<string, TrendInsight[]> = {
  "Health & wellness": [
    {
      niche: "Health & wellness",
      title: "Cortisol-aware morning routines",
      velocity: 92,
      competition: "Low",
      monetization: 78,
      source: "Reddit + TikTok Shop",
      blueprint: "Lead-magnet quiz → 7-day reset PDF → $47 protocol bundle",
    },
    {
      niche: "Health & wellness",
      title: "Mouth-taping & sleep stack",
      velocity: 81,
      competition: "Medium",
      monetization: 84,
      source: "Amazon Movers + YT Shorts",
      blueprint: "Bundle physical + digital sleep guide as $29 starter kit",
    },
  ],
  "Finance & investing": [
    {
      niche: "Finance & investing",
      title: "AI-assisted personal CFO templates",
      velocity: 88,
      competition: "Low",
      monetization: 91,
      source: "Gumroad + X creators",
      blueprint: "Notion template + Loom walkthrough at $79, upsell coaching",
    },
  ],
  "AI & tech": [
    {
      niche: "AI & tech",
      title: "No-code agent stacks for solopreneurs",
      velocity: 95,
      competition: "Medium",
      monetization: 88,
      source: "Product Hunt + Indie Hackers",
      blueprint: "Cohort-based mini-course $149 → SaaS template upsell",
    },
    {
      niche: "AI & tech",
      title: "Voice-clone podcast workflows",
      velocity: 76,
      competition: "Low",
      monetization: 72,
      source: "YouTube + Reddit",
      blueprint: "Free workflow PDF → $39 prompt pack → $199 setup service",
    },
  ],
  "Beauty & fashion": [
    {
      niche: "Beauty & fashion",
      title: "Glass-skin minimalism stacks",
      velocity: 84,
      competition: "High",
      monetization: 70,
      source: "TikTok Shop + Pinterest",
      blueprint: "Affiliate bundle landing page + branded routine ebook",
    },
  ],
  "Home & lifestyle": [
    {
      niche: "Home & lifestyle",
      title: "Quiet-luxury rental upgrades",
      velocity: 73,
      competition: "Medium",
      monetization: 68,
      source: "Pinterest + Etsy",
      blueprint: "Etsy printables pack → upsell room-by-room guide $24",
    },
  ],
  "Education & coaching": [
    {
      niche: "Education & coaching",
      title: "1:1 'micro-cohorts' under 10 seats",
      velocity: 80,
      competition: "Low",
      monetization: 93,
      source: "X + Skool communities",
      blueprint: "Waitlist landing → $499 pilot cohort → evergreen $199",
    },
  ],
  Gaming: [
    {
      niche: "Gaming",
      title: "Handheld PC accessory bundles",
      velocity: 79,
      competition: "Medium",
      monetization: 74,
      source: "YouTube + Reddit",
      blueprint: "Affiliate-first review hub → branded sleeve at $34",
    },
  ],
  Pets: [
    {
      niche: "Pets",
      title: "Senior-dog enrichment kits",
      velocity: 71,
      competition: "Low",
      monetization: 76,
      source: "TikTok + Amazon",
      blueprint: "Subscription box $29/mo → digital training upsell",
    },
  ],
  Sustainability: [
    {
      niche: "Sustainability",
      title: "Repair-economy starter toolkits",
      velocity: 68,
      competition: "Low",
      monetization: 65,
      source: "Reddit + Substack",
      blueprint: "Newsletter lead magnet → affiliate toolkit + $19 guide",
    },
  ],
};

const GOAL_HINT: Record<string, string> = {
  "Launch a new product": "Prioritized by low competition × high monetization.",
  "Scale an existing offer": "Prioritized by velocity to ride momentum.",
  "Grow content & audience": "Prioritized by velocity for hook-worthy angles.",
  "Find winning ad angles": "Prioritized by velocity × monetization for ROAS.",
};

function rankInsights(prefs: Preferences): TrendInsight[] {
  const pool = (prefs.niches.length ? prefs.niches : Object.keys(NICHE_INSIGHTS))
    .flatMap((n) => NICHE_INSIGHTS[n] ?? []);

  const score = (i: TrendInsight) => {
    const compPenalty = i.competition === "Low" ? 0 : i.competition === "Medium" ? 10 : 22;
    switch (prefs.primary_goal) {
      case "Launch a new product":
        return i.monetization * 0.6 + (100 - compPenalty * 3) * 0.4;
      case "Scale an existing offer":
        return i.velocity * 0.7 + i.monetization * 0.3;
      case "Grow content & audience":
        return i.velocity * 0.85 - compPenalty;
      case "Find winning ad angles":
        return i.velocity * 0.5 + i.monetization * 0.5 - compPenalty;
      default:
        return i.velocity * 0.4 + i.monetization * 0.4 - compPenalty;
    }
  };

  return [...pool].sort((a, b) => score(b) - score(a)).slice(0, 6);
}

type Watch = {
  id: string;
  kind: "niche" | "blueprint";
  label: string;
  niche: string | null;
  source: string | null;
  created_at: string;
};

export default function Dashboard() {
  const { user } = useAuth();
  const { isAdmin, isModerator } = useRole();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [draftNiches, setDraftNiches] = useState<string[]>([]);
  const [draftRole, setDraftRole] = useState<string>("");
  const [draftGoal, setDraftGoal] = useState<string>("");
  const [draftExperience, setDraftExperience] = useState<string>("");
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [watches, setWatches] = useState<Watch[]>([]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: prefData }, { data: watchData }] = await Promise.all([
        supabase
          .from("user_preferences")
          .select("role, niches, primary_goal, experience_level, onboarded_at")
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("watches")
          .select("id, kind, label, niche, source, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false }),
      ]);
      setPrefs(
        (prefData as Preferences) ?? {
          role: null,
          niches: [],
          primary_goal: null,
          experience_level: null,
          onboarded_at: null,
        }
      );
      setWatches((watchData as Watch[]) ?? []);
      setLoading(false);
    })();
  }, [user]);

  // Realtime subscription on watches
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`watches-feed:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "watches", filter: `user_id=eq.${user.id}` },
        (payload) => {
          setWatches((prev) => {
            if (payload.eventType === "INSERT") {
              const row = payload.new as Watch;
              if (prev.some((w) => w.id === row.id)) return prev;
              return [row, ...prev];
            }
            if (payload.eventType === "DELETE") {
              const row = payload.old as Watch;
              return prev.filter((w) => w.id !== row.id);
            }
            return prev;
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const addWatch = async (w: Omit<Watch, "id" | "created_at">) => {
    if (!user) return;
    const { error } = await supabase
      .from("watches")
      .insert({ ...w, user_id: user.id });
    if (error) {
      toast.error(error.message);
      return;
    }
    trackEvent(ANALYTICS_EVENTS.WATCH_ADDED, { kind: w.kind, niche: w.niche || "" });
    toast.success(`Watching "${w.label}"`);
  };

  const removeWatch = async (id: string) => {
    const { error } = await supabase.from("watches").delete().eq("id", id);
    if (error) toast.error(error.message);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate("/", { replace: true });
  };

  const openEdit = () => {
    setDraftNiches(prefs?.niches ?? []);
    setDraftRole(prefs?.role ?? "");
    setDraftGoal(prefs?.primary_goal ?? "");
    setDraftExperience(prefs?.experience_level ?? "");
    setEditOpen(true);
  };

  const toggleDraftNiche = (n: string) => {
    setDraftNiches((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].slice(0, 5)
    );
  };

  const savePrefs = async () => {
    if (!user) return;
    setSavingPrefs(true);
    try {
      const next = {
        user_id: user.id,
        niches: draftNiches,
        role: draftRole || null,
        primary_goal: draftGoal || null,
        experience_level: draftExperience || null,
        onboarded_at: prefs?.onboarded_at ?? new Date().toISOString(),
      };
      const { error } = await supabase
        .from("user_preferences")
        .upsert(next, { onConflict: "user_id" });
      if (error) throw error;
      setPrefs({
        role: next.role,
        niches: next.niches,
        primary_goal: next.primary_goal,
        experience_level: next.experience_level,
        onboarded_at: next.onboarded_at,
      });
      setEditOpen(false);
      toast.success("Preferences updated");
    } catch (err: any) {
      toast.error(err.message || "Could not update preferences");
    } finally {
      setSavingPrefs(false);
    }
  };

  const insights = prefs ? rankInsights(prefs) : [];
  const needsOnboarding = !loading && prefs && !prefs.onboarded_at;
  const { stats: trendStats, loading: trendsLoading, error: trendsError, stale: trendsStale, updatedAt: trendsUpdatedAt, refetch: refetchTrends } = useApprovedTrends();
  const hasCuratedTrends = !trendsLoading && !trendsError && trendStats.total > 0;
  const statErrorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (trendsError) statErrorRef.current?.focus();
  }, [trendsError]);


  return (
    <>
      <Helmet>
        <title>Dashboard — Trends Discovery</title>
        <meta name="description" content="Your live trend feed, alerts, and launch blueprints. Track rising niches and competitor signals across 35+ sources in real time." />
        <link rel="canonical" href="https://trendsdiscovery.com/dashboard" />
        <meta property="og:title" content="Dashboard — Trends Discovery" />
        <meta property="og:url" content="https://trendsdiscovery.com/dashboard" />
        <meta name="robots" content="noindex" />
      </Helmet>
    <div className="min-h-screen">
      <header className="border-b border-border">
        <div className="container flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-3 sm:py-4">
          <div className="font-display text-lg sm:text-xl font-bold whitespace-nowrap">
            Trends Discovery <span className="text-primary">/</span>{" "}
            <span className="text-muted-foreground text-xs sm:text-sm font-mono">dashboard</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 text-sm text-muted-foreground">
            <span className="font-mono hidden md:inline truncate max-w-[180px]">{user?.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/support")}
              title="Help & support"
              aria-label="Help"
            >
              <LifeBuoy className="size-4 sm:mr-1" />
              <span className="hidden sm:inline">Help</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/marketplace")}
              title="Browse trends and blueprints"
            >
              Marketplace
            </Button>
            {(isAdmin || isModerator) && (
              <Button variant="outline" size="sm" onClick={() => navigate("/admin")}>
                Admin
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={signOut}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-10 space-y-10">
        {/* HERO + QUICK SEARCH */}
        <section className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 md:p-8 bg-hero-radial">
          <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
          <div className="relative">
            <div className="font-mono text-xs text-primary mb-3 flex items-center gap-2">
              <span className="inline-block size-1.5 rounded-full bg-primary animate-pulse-dot" />
              LIVE · scanning 35+ secret sources
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-bold">
              {prefs?.role
                ? `Welcome back, ${prefs.role.toLowerCase()}.`
                : "Welcome in."}
            </h1>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              {prefs?.primary_goal
                ? GOAL_HINT[prefs.primary_goal] ?? "Tailored to your onboarding goals."
                : "Tailored to your onboarding goals."}
            </p>

            <QuickSearch insights={insights} />
          </div>
        </section>

        {needsOnboarding && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 flex items-center justify-between gap-4">
            <div className="text-sm">Finish onboarding to unlock fully tailored picks.</div>
            <Button size="sm" variant="hero" onClick={() => navigate("/onboarding")}>
              Finish setup
            </Button>
          </div>
        )}

        {/* STATS — numbers derive from the same approved-trends query as the curated feed */}
        <section
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="dashboard-stats"
        >
          <div className="col-span-2 lg:col-span-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span data-testid="stat-curated-age" role="status">
              {trendsStale ? "Cached counts from " : "Counts updated "}
              {trendsUpdatedAt ? formatDataAge(trendsUpdatedAt) : "unknown"}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="min-h-11"
              data-testid="stat-curated-refresh"
              aria-label="Refresh curated trend counts"
              aria-busy={trendsLoading}
              disabled={trendsLoading}
              onClick={() => refetchTrends()}
            >
              <RefreshCw className={`size-3.5 mr-1.5 ${trendsLoading ? "animate-spin" : ""}`} aria-hidden="true" />
              {trendsLoading ? "Refreshing" : "Refresh"}
            </Button>
          </div>
          {trendsStale && (
            <div
              data-testid="stat-curated-stale"
              role="status"
              className="col-span-2 lg:col-span-4 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground flex items-center gap-2"
            >
              <AlertTriangle className="size-3.5" aria-hidden="true" />
              Live data is temporarily unavailable — showing your last known counts.
            </div>
          )}
          <StatCard
            icon={<Radar className="size-4" />}
            label="Sources scanned"
            value="35"

            sub="last sweep · 4m ago"
          />
          {hasCuratedTrends ? (
            <>
              <StatCard
                icon={<TrendingUp className="size-4" />}
                label="New trends today"
                value={String(trendStats.newToday)}
                sub={`of ${trendStats.total} curated`}
                testId="stat-new-trends-today"
              />
              <StatCard
                icon={<FileText className="size-4" />}
                label="Blueprints ready"
                value={String(trendStats.blueprintsReady)}
                sub="actionable playbooks"
                testId="stat-blueprints-ready"
              />
            </>
          ) : trendsError ? (
            <div
              className="col-span-2 rounded-xl border border-destructive/40 bg-destructive/5 p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="stat-curated-error"
              role="alert"
              aria-labelledby="stat-curated-error-title"
              aria-describedby="stat-curated-error-desc"
              tabIndex={-1}
              ref={statErrorRef}
            >
              <h2
                id="stat-curated-error-title"
                className="font-mono text-xs text-destructive flex items-center gap-2"
              >
                <AlertTriangle className="size-4" aria-hidden="true" />
                CURATED TRENDS UNAVAILABLE
              </h2>
              <div id="stat-curated-error-desc" className="mt-2 text-sm text-muted-foreground">
                We couldn&apos;t load your trend and blueprint counts. {trendsError}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-3 min-h-11"
                data-testid="stat-curated-retry"
                aria-label="Retry loading curated trend counts"
                aria-busy={trendsLoading}
                disabled={trendsLoading}
                onClick={() => refetchTrends()}
              >
                <RefreshCw
                  className={`size-3.5 mr-1.5 ${trendsLoading ? "animate-spin" : ""}`}
                  aria-hidden="true"
                />{" "}
                {trendsLoading ? "Retrying" : "Retry"}
              </Button>
              <p className="sr-only" role="status">
                {trendsLoading
                  ? "Retrying to load curated trend counts"
                  : "Curated trend counts failed to load"}
              </p>
            </div>
          ) : (

            <div
              className="col-span-2 rounded-xl border border-border bg-card p-5"
              data-testid="stat-curated-empty"
            >
              <div className="font-mono text-xs text-muted-foreground flex items-center gap-2">
                <Sparkles className="size-4 text-primary" />
                CURATED TRENDS
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {trendsLoading
                  ? "Loading curated trends…"
                  : "No curated trends yet — our scanners are still pulling signals. Trend and blueprint counts appear here as soon as trends are approved for your account."}
              </div>
            </div>
          )}
          <StatCard
            icon={<Bell className="size-4" />}
            label="Active alerts"
            value={String(prefs?.niches.length || 0)}
            sub="watching your niches"
          />
        </section>

        <LicenseStatusPanel />

        {prefs && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground mr-1">tracking:</span>
            {prefs.niches.length > 0 ? (
              prefs.niches.map((n) => (
                <span
                  key={n}
                  className="text-xs font-mono rounded-full border border-border px-3 py-1 text-muted-foreground"
                >
                  {n}
                </span>
              ))
            ) : (
              <span className="text-xs font-mono text-muted-foreground">
                No niches selected yet
              </span>
            )}
            <Button size="sm" variant="outline" onClick={openEdit} className="ml-1">
              Edit preferences
            </Button>
          </div>
        )}

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit preferences</DialogTitle>
              <DialogDescription>
                Update your role, goal, and niches. Your ranked list updates instantly.
              </DialogDescription>
            </DialogHeader>

            <PrefSection label="Role">
              <ChipGrid
                options={ROLE_OPTIONS}
                selected={draftRole ? [draftRole] : []}
                onSelect={setDraftRole}
              />
            </PrefSection>

            <PrefSection label="Primary goal">
              <ChipGrid
                options={GOAL_OPTIONS}
                selected={draftGoal ? [draftGoal] : []}
                onSelect={setDraftGoal}
              />
            </PrefSection>

            <PrefSection label="Experience level">
              <ChipGrid
                options={EXPERIENCE_OPTIONS}
                selected={draftExperience ? [draftExperience] : []}
                onSelect={setDraftExperience}
              />
            </PrefSection>

            <PrefSection label="Niches" hint="Pick up to 5">
              <ChipGrid
                options={NICHE_OPTIONS}
                selected={draftNiches}
                onSelect={toggleDraftNiche}
                multi
              />
            </PrefSection>

            <PrefSection label="Live preview" hint="How your ranked list will look">
              <RankPreview
                current={prefs ? rankInsights(prefs) : []}
                next={rankInsights({
                  role: draftRole || null,
                  niches: draftNiches,
                  primary_goal: draftGoal || null,
                  experience_level: draftExperience || null,
                  onboarded_at: prefs?.onboarded_at ?? null,
                })}
              />
            </PrefSection>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button variant="hero" onClick={savePrefs} disabled={savingPrefs}>
                {savingPrefs ? "Saving…" : "Save & re-rank"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <NotificationSettingsPanel />
        <NotificationHistoryPanel />
        <TrendDetailsModal />

        <LiveTrendsFeed />
        <ShareAnalyticsPanel />
        <ChannelAnalyticsPanel />
        <ReferralPanel />
        <TrendAlertsPanel />
        <SuccessStories />

        <section>
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h2 className="font-display text-2xl font-bold">Ranked niche list</h2>
              <p className="text-xs text-muted-foreground font-mono mt-1">
                // top opportunities, freshly scored
              </p>
            </div>
            <div className="font-mono text-xs text-muted-foreground">
              {loading ? "scanning…" : `${insights.length} picks`}
            </div>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-40 rounded-xl border border-border bg-card animate-pulse"
                />
              ))}
            </div>
          ) : insights.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
              No insights yet. Pick a few niches in onboarding to seed your feed.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {insights.map((i, idx) => (
                <article
                  key={`${i.niche}-${i.title}`}
                  className="group rounded-xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-elevated transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-xs text-primary">
                      #{String(idx + 1).padStart(2, "0")} · {i.niche}
                    </div>
                    <div
                      className={`text-[10px] font-mono uppercase rounded px-1.5 py-0.5 border ${
                        i.competition === "Low"
                          ? "border-primary/40 text-primary"
                          : i.competition === "Medium"
                          ? "border-border text-muted-foreground"
                          : "border-destructive/40 text-destructive"
                      }`}
                    >
                      {i.competition} comp
                    </div>
                  </div>
                  <h3 className="font-display text-lg font-semibold mt-2">{i.title}</h3>

                  <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                    <Metric label="Velocity" value={i.velocity} />
                    <Metric label="Monetization" value={i.monetization} />
                  </div>

                  <div className="mt-4 text-xs text-muted-foreground font-mono">
                    src · {i.source}
                  </div>
                  <p className="mt-3 text-sm">{i.blueprint}</p>

                  <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                    <button
                      type="button"
                      onClick={() =>
                        addWatch({
                          kind: "blueprint",
                          label: i.title,
                          niche: i.niche,
                          source: i.source,
                        })
                      }
                      className="text-xs font-mono text-primary inline-flex items-center gap-1 hover:underline"
                    >
                      Save blueprint <ArrowUpRight className="size-3" />
                    </button>
                    {(() => {
                      const existing = watches.find(
                        (w) => w.kind === "niche" && w.label === i.niche
                      );
                      return (
                        <button
                          type="button"
                          onClick={() =>
                            existing
                              ? removeWatch(existing.id)
                              : addWatch({
                                  kind: "niche",
                                  label: i.niche,
                                  niche: i.niche,
                                  source: i.source,
                                })
                          }
                          className={`text-xs font-mono inline-flex items-center gap-1 ${
                            existing
                              ? "text-primary"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <Bell className="size-3" /> {existing ? "Watching" : "Watch"}
                        </button>
                      );
                    })()}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* BLUEPRINTS + ALERTS */}
        <section className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="size-4 text-primary" />
                <h2 className="font-display text-xl font-bold">Your blueprints</h2>
              </div>
              <span className="font-mono text-xs text-muted-foreground">
                {insights.length} ready
              </span>
            </div>
            {insights.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                Blueprints unlock once you pick niches.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {insights.slice(0, 4).map((i, idx) => (
                  <li
                    key={`bp-${i.title}`}
                    className="py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-mono text-[10px] text-muted-foreground">
                        BP-{String(idx + 1).padStart(3, "0")} · {i.niche}
                      </div>
                      <div className="text-sm truncate">{i.title}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toast.success(`Opening blueprint…`)}
                      className="text-xs font-mono text-primary inline-flex items-center gap-1 shrink-0 hover:underline"
                    >
                      Open <ArrowUpRight className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bell className="size-4 text-accent" />
                <h2 className="font-display text-xl font-bold">Alerts</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-muted-foreground">
                  {watches.length} watching
                </span>
                <span className="inline-block size-1.5 rounded-full bg-accent animate-pulse-dot" />
              </div>
            </div>
            {watches.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No alerts yet. Tap <span className="font-mono">Watch</span> on a trend or
                <span className="font-mono"> Save blueprint</span> to start tracking.
              </div>
            ) : (
              <ul className="space-y-3">
                {watches.slice(0, 6).map((w) => (
                  <li
                    key={w.id}
                    className="rounded-lg border border-border bg-background p-3 animate-float-up"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] text-accent uppercase">
                        {w.kind === "niche" ? "Niche watch" : "Blueprint saved"}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {timeAgo(w.created_at)}
                      </span>
                    </div>
                    <div className="text-sm mt-1 truncate">{w.label}</div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="font-mono text-[10px] text-muted-foreground truncate">
                        {[w.niche, w.source].filter(Boolean).join(" · ")}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeWatch(w.id)}
                        className="font-mono text-[10px] text-muted-foreground hover:text-destructive"
                      >
                        remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
    </>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="flex items-center justify-between font-mono text-[10px] uppercase text-muted-foreground">
        <span>{label}</span>
        <span>{value}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-border overflow-hidden">
        <div
          className="h-full bg-primary"
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      </div>
    </div>
  );
}

function PrefSection({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4">
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-mono text-xs text-primary">{label}</div>
        {hint && <div className="text-[10px] font-mono text-muted-foreground">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function ChipGrid({
  options,
  selected,
  onSelect,
  multi = false,
}: {
  options: string[];
  selected: string[];
  onSelect: (value: string) => void;
  multi?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onSelect(opt)}
            aria-pressed={active}
            className={`text-left text-sm rounded-lg border px-3 py-2.5 transition-colors ${
              active
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border bg-background hover:border-primary/50"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function RankPreview({
  current,
  next,
}: {
  current: { niche: string; title: string }[];
  next: { niche: string; title: string }[];
}) {
  const keyOf = (i: { niche: string; title: string }) => `${i.niche}::${i.title}`;
  const currentIndex = new Map(current.map((i, idx) => [keyOf(i), idx]));
  const nextKeys = new Set(next.map(keyOf));
  const removed = current.filter((i) => !nextKeys.has(keyOf(i)));

  if (next.length === 0 && removed.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-background p-3 text-xs text-muted-foreground">
        Pick at least one niche to preview your ranked list.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-background p-2 space-y-1">
      {next.map((item, idx) => {
        const key = keyOf(item);
        const prev = currentIndex.get(key);
        let label = "•";
        let tone = "border-border text-muted-foreground";
        if (prev === undefined) {
          label = "NEW";
          tone = "border-primary/40 text-primary";
        } else if (prev > idx) {
          label = `↑${prev - idx}`;
          tone = "border-primary/40 text-primary";
        } else if (prev < idx) {
          label = `↓${idx - prev}`;
          tone = "border-destructive/40 text-destructive";
        }
        return (
          <div key={key} className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[10px] text-muted-foreground w-6">
                #{String(idx + 1).padStart(2, "0")}
              </span>
              <span className="truncate">{item.title}</span>
              <span className="font-mono text-[10px] text-muted-foreground hidden sm:inline">
                · {item.niche}
              </span>
            </div>
            <span className={`font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border ${tone}`}>
              {label}
            </span>
          </div>
        );
      })}
      {removed.map((item) => (
        <div
          key={`removed-${keyOf(item)}`}
          className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-xs opacity-60"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-[10px] text-muted-foreground w-6">—</span>
            <span className="truncate line-through">{item.title}</span>
          </div>
          <span className="font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border border-destructive/40 text-destructive">
            REMOVED
          </span>
        </div>
      ))}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  sub,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  testId?: string;
}) {
  return (
    <div
      className="rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-center justify-between text-muted-foreground">
        <span className="font-mono text-[10px] uppercase">{label}</span>
        <span className="text-primary">{icon}</span>
      </div>
      <div className="font-display text-3xl font-bold mt-2" data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function QuickSearch({ insights }: { insights: TrendInsight[] }) {
  const [q, setQ] = useState("");
  const results = useMemo(() => {
    if (!q.trim()) return [];
    const needle = q.toLowerCase();
    return insights
      .filter(
        (i) =>
          i.title.toLowerCase().includes(needle) ||
          i.niche.toLowerCase().includes(needle) ||
          i.source.toLowerCase().includes(needle)
      )
      .slice(0, 5);
  }, [q, insights]);

  const suggestions = ["AI agents", "sleep stack", "cohort", "Etsy", "TikTok Shop"];

  return (
    <div className="mt-6 max-w-2xl">
      <label className="relative block">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search trends, niches, sources…"
          className="pl-10 h-12 bg-background/60 backdrop-blur border-border focus-visible:ring-primary"
        />
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-block font-mono text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5">
          ⏎
        </kbd>
      </label>

      {q.trim() ? (
        <div className="mt-3 rounded-lg border border-border bg-background/80 backdrop-blur divide-y divide-border">
          {results.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">
              No matches in your feed. Try a broader term.
            </div>
          ) : (
            results.map((r) => (
              <button
                key={r.title}
                type="button"
                onClick={() => toast.success(`Opening "${r.title}"`)}
                className="w-full text-left p-3 hover:bg-muted/30 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="text-sm truncate">{r.title}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {r.niche} · {r.source}
                  </div>
                </div>
                <ArrowUpRight className="size-3 text-primary shrink-0" />
              </button>
            ))
          )}
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Sparkles className="size-3 text-primary" />
          <span className="font-mono text-[10px] text-muted-foreground">try:</span>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setQ(s)}
              className="text-xs font-mono rounded-full border border-border px-2.5 py-1 text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
